<script lang="ts">
import CountrySelect from '@/app/common/components/CountrySelect.vue';
import { STEP_TWO, TITLE_OPTIONS } from '@/app/const';
import { BaseConfirm } from '@/interface/BaseConfirm.interface';
import { ComplaintInfo } from '@/interface/Complaint.interface';
import { PassengerInfo } from '@/interface/Passenger.interface';
import { userService } from '@/services/serviceProvider';

export default {
    components: {
        CountrySelect
    },
    setup() {
        return {}
    },
    props: {
        modelValue: {
            type: Object,
            default: {},
        },
        viewMode: {
            type: Boolean,
            default: false
        },
        hideInfo: {
            type: Boolean,
            default: false
        },
        isManage: {
            type: Boolean,
            default: false
        }
    },
    emits: [
        'update:modelValue',
        'displayConfirmDialog',
    ],
    data() {
        return {
            model: this.modelValue as ComplaintInfo,
            currentIndexDeleting: -1 as number,
            stepTwo: STEP_TWO,
            titleOptions: TITLE_OPTIONS,
            rules: {
                required: (message: any) => {
                    return (v: any) => !!v || message;
                },
                minLengthRule(message: any) {
                    return (v: any) => (v && v.length >= 10) || message;
                },
                emailRule(message: any) {
                    return (v: any) =>
                        !!v.match(
                            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
                        ) || message;
                },
            },
        }
    },
    watch: {
        modelValue(newVal, oldVal) {
            if (newVal) {
                this.model = newVal;
            }
        },
    },
    computed: {
        sections(): number[] {
            return this.model.form?.stepTwo ?? [];
        },
        emptyPassenger() {
            let enablePublished = this.enableSection(this.stepTwo.Published);
            return {
                title: undefined,
                firstName: undefined,
                lastName: undefined,
                nationality: undefined,
                phoneNumber: undefined,
                email: undefined,
                published: !enablePublished ? true : false,
                revealInfo: false,
            } as PassengerInfo
        },
    },
    async mounted() {
        if (!this.model.passengers || this.model.passengers.length === 0)
            this.addPassenger();
    },
    methods: {
        addPassenger() {
            let p = { ...this.emptyPassenger };
            p.itemNo = -1;
            this.model.passengers.push(p);
        },
        clearInfo() {
            let itemNo = this.model.passengers[this.currentIndexDeleting].itemNo;
            this.model.passengers[this.currentIndexDeleting] = { ...this.emptyPassenger };
            this.model.passengers[this.currentIndexDeleting].itemNo = itemNo;
        },
        deletePassenger() {
            this.model.passengers.splice(this.currentIndexDeleting, 1);
        },
        selfInfo(index: number, value: boolean) {
            const tempItemNo = this.model.passengers[index].itemNo;
            const tempPublished = this.model.passengers[index].published;
            if (value !== undefined && value === true) {
                const user = userService.currentUserInfo();
                if (user) {
                    this.model.passengers[index] = {
                        itemNo: tempItemNo,
                        title: user.title && user.title !== '' ? user.title : undefined,
                        firstName: user.firstname,
                        middleName: user.midname,
                        lastName: user.lastname,
                        nationality: user.nation,
                        email: user.email,
                        phoneNumber: user.tel,
                        revealInfo: true,
                        published: tempPublished,
                    } as PassengerInfo
                }
            } else {
                this.model.passengers[index] = { ...this.emptyPassenger };
                this.model.passengers[index].itemNo = tempItemNo;
                this.model.passengers[index].published = tempPublished;
            }
        },
        confirmClearPassenger() {
            this.$emit('displayConfirmDialog', {
                messageTitle: `${this.$t('t-you-need')}${this.$t('t-clear')}${this.$t('t-complaint-this-passenger')}${this.$t('t-or-not')}`,
                messageText: undefined,
                callBack: this.clearInfo,
            } as BaseConfirm)
        },
        confirmDeletePassenger() {
            this.$emit('displayConfirmDialog', {
                messageTitle: `${this.$t('t-you-need')}${this.$t('t-delete')}${this.$t('t-complaint-this-passenger')}${this.$t('t-or-not')}`,
                messageText: undefined,
                callBack: this.deletePassenger,
            } as BaseConfirm)
        },
        enableSection(code: number) {
            return this.sections.includes(code);
        },
        filterPhoneInput(index: number, value: string | undefined | null) {
            this.model.passengers[index].phoneNumber = value?.replace(/[^0-9]/g, ""); // Strip out non-numeric characters
        },
        hidePersonalInfo(passenger: PassengerInfo){
            return !passenger.published && userService.isAirline();
        }
    },
};
</script>
<template>
    <v-card v-if="enableSection(stepTwo.Detail)" class="app-card pt-2" :class="isManage ? 'hide-card' : ''"
        v-for="(passenger, index) in model.passengers">
        <div v-if="!hidePersonalInfo(passenger)" class="d-flex justify-space-between align-center">
            <div class="d-flex align-center">
                <v-toolbar class="mr-6" style="width: fit-content;" density="compact">
                    <v-toolbar-title class="ml-0">
                        {{ $t('t-complaint-passenger-information') + $t('t-person-no') }}
                        {{ index + 1 }}
                    </v-toolbar-title>
                </v-toolbar>
                <div class="passenger-option-toolbar">
                    <div v-if="index === 0 || enableSection(stepTwo.Published)" class="splitter my-4 mr-2"></div>
                    <v-checkbox v-if="index === 0" v-model="passenger.revealInfo" :label="$t('t-user-self-info')"
                        class="mr-2" hide-details :readonly="viewMode"
                        @change="selfInfo(index, passenger.revealInfo)"></v-checkbox>
                    <!-- if this section hide -> default published is true -->
                    <v-checkbox v-if="enableSection(stepTwo.Published)" v-model="passenger.published" class="pr-3"
                        :label="$t('t-user-published')" :readonly="viewMode" hide-details></v-checkbox>
                </div>
            </div>
            <v-btn v-if="index === 0 && !viewMode" variant="text" color="primary"
                @click="currentIndexDeleting = index; confirmClearPassenger();">
                <v-icon class="mr-2" icon="mdi mdi-backspace-outline"></v-icon>
                {{ $t('t-clear') }}
            </v-btn>
            <v-btn v-else-if="index !== 0 && !viewMode" variant="text" color="primary"
                @click="currentIndexDeleting = index; confirmDeletePassenger();">
                <v-icon class="mr-2" icon="mdi mdi-delete-outline"></v-icon>
                {{ $t('t-delete') }}
            </v-btn>
        </div>
        <v-divider v-if="!hidePersonalInfo(passenger)"></v-divider>
        <v-card-text v-if="!hideInfo && !hidePersonalInfo(passenger)">
            <div class="passenger-option-mobile mb-3">
                <v-checkbox v-if="index === 0" v-model="passenger.revealInfo" :label="$t('t-user-self-info')"
                    class="mr-2" hide-details :readonly="viewMode" density="compact"
                    @change="selfInfo(index, passenger.revealInfo)"></v-checkbox>
                <v-checkbox v-if="enableSection(stepTwo.Published)" v-model="passenger.published" class="pr-3"
                    :label="$t('t-user-published')" :readonly="viewMode" density="compact" hide-details></v-checkbox>
            </div>
            <v-row>
                <v-col cols="12" sm="2" lg="2">
                    <div class="text-label">{{ $t('t-user-title') }} *</div>
                    <v-select v-model="passenger.title" class="hide-float-label" variant="outlined"
                        v-bind:placeholder="$t('t-please-select')" hide-details="auto" :items="titleOptions"
                        item-value="value" item-title="label" density="comfortable" :readonly="viewMode"
                        :rules="viewMode ? [] : [rules.required(`${$t('t-please-select')}${$t('t-user-title')}`)]">
                        <template v-slot:item="{ props, item }">
                            <v-list-item v-bind="props" :title="$t(`t-${item.raw.label}`)"></v-list-item>
                        </template>
                        <template v-slot:selection="{ item }">
                            <span>{{ $t(`t-${item.raw.label}`) }}</span>
                        </template>
                    </v-select>
                </v-col>
                <v-col cols="12" sm="10" lg="8">
                    <v-row>
                        <v-col cols="12" sm="4" lg="4">
                            <div class="text-label">{{ $t('t-user-first-name') }} *</div>
                            <v-text-field v-model.trim="passenger.firstName" density="comfortable" variant="outlined"
                                hide-details="auto" class="hide-float-label" v-bind:placeholder="$t('t-please-input')"
                                :readonly="viewMode"
                                :rules="viewMode ? [] : [rules.required(`${$t('t-please-fill')}${$t('t-user-first-name')}`)]"></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="4" lg="4">
                            <div class="text-label">{{ $t('t-user-middle-name') }}</div>
                            <v-text-field v-model.trim="passenger.middleName" density="comfortable" variant="outlined"
                                hide-details="auto" class="hide-float-label" :readonly="viewMode"
                                v-bind:placeholder="$t('t-please-input')"></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="4" lg="4">
                            <div class="text-label">{{ $t('t-user-last-name') }} *</div>
                            <v-text-field v-model.trim="passenger.lastName" density="comfortable" variant="outlined"
                                hide-details="auto" class="hide-float-label" v-bind:placeholder="$t('t-please-input')"
                                :readonly="viewMode"
                                :rules="viewMode ? [] : [rules.required(`${$t('t-please-fill')}${$t('t-user-last-name')}`)]"></v-text-field>
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="12" sm="3" lg="2">
                    <div class="text-label">{{ $t('t-user-nationality') }} *</div>
                    <CountrySelect v-model="passenger.nationality" :view-mode="viewMode" />
                </v-col>
                <v-col cols="12" sm="3" lg="6">
                    <div class="text-label">{{ $t('t-user-phone') }} *</div>
                    <v-text-field v-model.trim="passenger.phoneNumber" density="comfortable" variant="outlined"
                        hide-details="auto" class="hide-float-label" v-bind:placeholder="$t('t-please-input')"
                        :readonly="viewMode" @input="filterPhoneInput(index, passenger.phoneNumber)" maxlength="10"
                        :rules="viewMode ? []
                            : passenger.nationality === 'th' ? [rules.required(`${$t('t-please-fill')}${$t('t-user-phone')}`), 
                                rules.minLengthRule($t('t-please-fill-correct-phone'))] : []">
                    </v-text-field>
                </v-col>
                <v-col cols="12" sm="6" lg="6">
                    <div class="text-label">{{ $t('t-user-email-full') }} *</div>
                    <v-text-field v-model.trim="passenger.email" density="comfortable" variant="outlined"
                        hide-details="auto" class="hide-float-label" v-bind:placeholder="$t('t-please-input')"
                        :readonly="viewMode"
                        :rules="viewMode ? [] : [rules.required(`${$t('t-please-fill')}${$t('t-user-email-full')}`), 
                            rules.emailRule($t('t-please-fill-correct-email'))]"></v-text-field>
                </v-col>
            </v-row>
        </v-card-text>
        <div v-if="hidePersonalInfo(passenger)">
            <span class="mr-2">
                {{ $t('t-complaint-passenger-information') + $t('t-person-no') }} {{ index + 1 }}
            </span>
            <span class="text-primary">{{ $t('t-user-not-published') }}</span>
        </div>
    </v-card>
    <div v-if="!viewMode" @click="addPassenger"
        class="w-100 border d-flex align-center justify-center py-6 add-passenger">
        <v-icon icon="mdi mdi-plus mr-1"></v-icon>{{ $t('t-add-passenger') }}
    </div>
</template>